<template>
  <div class="inline-flex items-center space-x-3">
    <!-- Search Bar Component -->
    <SearchBar 
      v-if="showSearchbar" 
      :icon="searchIcon" 
      :placeholder="searchPlaceholder" 
      :show-text="showSearchText" 
      :text="searchText" 
      :initial-query="lastSearchQuery"
      @search="handleSearch"
      class="w-full sm:w-auto"
    />

    <!-- Sort Menu Components -->
    <SortMenu 
      v-if="showSort" 
      v-for="(option, index) in sortOptions" 
      :key="`sort-${option.id || index}`" 
      :option="option" 
      :selected="selectedSort" 
      @select="selectSortItem($event, option)"
      @clear="resetSort"
    />

    <!-- Filter Menu Components -->
    <FilterMenu 
      v-if="showFilter" 
      v-for="(option, index) in filterOptions" 
      :key="`filter-${option.id || index}`" 
      :option="option" 
      :selected="selectedFilters[option.id]" 
      @select="selectFilterItem(option.id, $event)"
    />
  </div>
</template>

<script setup>
import { computed, provide } from 'vue'
import SearchBar from './components/searchBar.vue'
import SortMenu from './components/sortMenu.vue'
import FilterMenu from './components/filterMenu.vue'

// Import composable
import { useFilters } from './composables'

const props = defineProps({
  // Search props
  searchIcon: { type: String, default: 'search' },
  searchPlaceholder: { type: String, default: 'Search...' },
  showSearchbar: { type: Boolean, default: true },
  showSearchText: { type: Boolean, default: false },
  searchText: { type: String, default: 'Search' },
  
  // Sort props
  showSort: { type: Boolean, default: true },
  sortOptions: { type: Array, default: () => [] },
  
  // Filter props
  showFilter: { type: Boolean, default: true },
  filterOptions: { type: Array, default: () => [] },
})

const emit = defineEmits(['search', 'sort', 'filter'])

// Initialize the filter composable
const {
  // State
  openMenu,
  lastSearchQuery,
  selectedSort,
  selectedFilters,
  
  // Methods
  handleSearch,
  selectSortItem,
  selectFilterItem,
  resetSort,
  resetFilters,
  resetAll,
} = useFilters({
  emitSearch: (query) => emit('search', query),
  emitSort: (sortData) => emit('sort', sortData),
  emitFilter: (filterData) => emit('filter', filterData),
  filterOptions: props.filterOptions,
})

// Provide openMenu for child components
provide('openMenu', openMenu)

// Computed property to determine if any filters are active
const hasActiveFilters = computed(() => {
  // Check if search is active
  if (lastSearchQuery.value) return true
  
  // Check if sort is active
  if (selectedSort.value) return true
  
  // Check if any filters are active
  for (const [key, value] of Object.entries(selectedFilters)) {
    if (value && Array.isArray(value) && value.length > 0) return true
  }
  
  return false
})

// Computed property to check if there are active filters that aren't defaults
const hasActiveNonDefaultFilters = computed(() => {
  // Check if search is active
  if (lastSearchQuery.value) return true
  
  // Check if sort is active
  if (selectedSort.value) return true
  
  // Check if any non-default filters are active
  for (const [key, values] of Object.entries(selectedFilters)) {
    if (!values || !Array.isArray(values) || values.length === 0) continue
    
    // Find the filter option
    const filterOption = props.filterOptions.find(opt => opt.id === key)
    if (!filterOption) continue
    
    // If any selected value is not a default, return true
    for (const value of values) {
      const item = filterOption.items?.find(item => item.id === value)
      if (item && !item.default) return true
    }
  }
  
  return false
})
</script>

<style scoped>
@media (max-width: 768px) {
  .inline-flex {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .space-x-3 > * + * {
    margin-left: 0;
  }
}
</style>
